<template>
  <div class="px-2 md:px-4 py-4 min-h-screen">
    <PageHeader
      show-back-button
      :back-handler="handleBack"
      :title="t('chromeExtension.title')"
      :description="t('chromeExtension.description')"
      :icon-path="ICONS.download"
    />

    <HLSpace vertical :size="24" class="mt-8 max-w-4xl">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 md:items-stretch">
        <!-- Download extension (left) -->
        <HLCard class="flex flex-col min-h-0 md:h-full border-l-4 border-l-blue-500 dark:border-l-blue-400 min-w-0">
          <div class="p-6 flex flex-col flex-1 min-h-0 min-w-0 overflow-x-hidden">
            <div class="flex flex-wrap items-start justify-between gap-4 mb-2">
              <HLSpace :size="12">
                <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400">
                  <Icon name="download" size="sm" class="w-5 h-5" />
                </div>
                <HLSpace vertical :size="4">
                  <HLText variant="h6" class="text-gray-900 dark:text-white font-semibold">
                    {{ t('chromeExtension.downloadTitle') }}
                  </HLText>
                  <HLText variant="body-sm" class="text-gray-500 dark:text-gray-400">
                    {{ t('chromeExtension.downloadDescription') }}
                  </HLText>
                </HLSpace>
              </HLSpace>
              <HLButton
                :disabled="loading"
                color="blue"
                variant="primary"
                size="md"
                class="shrink-0"
                @click="fetchReleases()"
              >
                <template #iconLeft>
                  <span v-if="loading" class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <Icon v-else name="refresh" size="sm" class="w-4 h-4" />
                </template>
                {{ t('chromeExtension.syncReleases') }}
              </HLButton>
            </div>
            <HLText variant="body-sm" class="font-medium text-gray-600 dark:text-gray-300 mb-2">
              {{ t('chromeExtension.versionsLabel') }}
            </HLText>
            <HLCard variant="secondary" class="flex-1 min-h-0 p-5 pt-8 flex flex-col w-full max-w-[88%] min-w-0 self-start overflow-y-auto mt-4">
              <HLSpin :show="loading && versions.length === 0" size="large">
                <template #description>
                  <HLText variant="body-sm" class="text-gray-500 dark:text-gray-400">
                    {{ t('chromeExtension.loadingReleases') }}
                  </HLText>
                </template>
                <div class="min-h-[100px] w-full max-w-full overflow-x-auto">
                  <HLDataTableWrapper responsive-column-width max-width="100%" class="chrome-extension-table-wrapper">
                    <HLDataTable
                      id="chrome-extension-releases-table"
                      :columns="extensionTableColumns"
                      :data="versions"
                      :loading="loading"
                      row-key="tagName"
                      :column-hover="false"
                      :horizontal-borders="true"
                      class="chrome-extension-releases-table"
                    >
                      <template #empty>
                        <div class="py-6">
                          <template v-if="error">
                            <HLSpace vertical :size="8">
                              <HLText variant="body-sm" class="font-semibold text-gray-700 dark:text-gray-300">
                                {{ t('chromeExtension.releasesError') }}
                              </HLText>
                              <HLText variant="body-sm" class="text-gray-500 dark:text-gray-500">
                                {{ error }}
                              </HLText>
                              <HLText variant="caption" class="text-gray-500 dark:text-gray-500">
                                {{ t('chromeExtension.releasesErrorHint') }}
                              </HLText>
                            </HLSpace>
                          </template>
                          <HLText v-else variant="body-sm" class="text-gray-500 dark:text-gray-400 block">
                            {{ t('chromeExtension.noReleases') }}
                          </HLText>
                        </div>
                      </template>
                    </HLDataTable>
                  </HLDataTableWrapper>
                </div>
              </HLSpin>
            </HLCard>
          </div>
        </HLCard>

        <!-- Add to Chrome (right) -->
        <HLCard class="flex flex-col min-h-0 md:h-full border-l-4 border-l-emerald-500 dark:border-l-emerald-400 min-w-0">
          <div class="p-6 flex flex-col flex-1 min-h-0 min-w-0 overflow-x-hidden">
            <HLSpace :size="12" class="mb-4">
              <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400">
                <Icon name="checkCircle" size="sm" class="w-5 h-5" />
              </div>
              <HLSpace vertical :size="4">
                <HLText variant="h6" class="text-gray-900 dark:text-white font-semibold">
                  {{ t('chromeExtension.installTitle') }}
                </HLText>
                <HLText variant="body-sm" class="text-gray-500 dark:text-gray-400">
                  {{ t('chromeExtension.installSubtitle') }}
                </HLText>
              </HLSpace>
            </HLSpace>
            <HLCard variant="secondary" class="flex-1 p-5 pt-8 min-h-[200px] w-full max-w-[88%] min-w-0 self-start mt-4">
              <HLSpace vertical :size="16">
                <HLSpace
                  v-for="(step, i) in installSteps"
                  :key="i"
                  :size="12"
                  align="start"
                  class="gap-3"
                >
                  <span class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 text-xs font-bold">
                    {{ i + 1 }}
                  </span>
                  <HLText variant="body-sm" class="text-gray-700 dark:text-gray-300 pt-0.5">
                    {{ step }}
                  </HLText>
                </HLSpace>
              </HLSpace>
              <HLText variant="caption" class="block mt-5 pt-4 border-t border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400">
                {{ t('chromeExtension.note') }}
              </HLText>
            </HLCard>
          </div>
        </HLCard>
      </div>
    </HLSpace>
  </div>
</template>

<script setup>
import { computed, h } from 'vue'
import { useRouter } from 'vue-router'
import PageHeader from '@/components/ui/PageHeader.vue'
import Icon from '@/components/ui/Icon.vue'
import { ICONS } from '@/constants/icons'
import { t } from '@/i18n/en-us'
import { useChromeExtensionReleases } from '@/composables/useChromeExtensionReleases'
import { HLCard, HLSpace, HLButton, HLText, HLSpin, HLDataTable, HLDataTableWrapper } from '@platform-ui/highrise'

const router = useRouter()
const { versions, loading, error, fetchReleases } = useChromeExtensionReleases(4)

const installSteps = computed(() => [
  t('chromeExtension.step1'),
  t('chromeExtension.step2'),
  t('chromeExtension.step3'),
  t('chromeExtension.step4'),
  t('chromeExtension.step5')
])

const extensionTableColumns = computed(() => [
  {
    id: 'version',
    accessorKey: 'version',
    header: t('chromeExtension.tableVersion'),
    width: '45%',
    minWidth: '120px',
    meta: { align: 'center' },
    cellFormatter: (props) => {
      const row = props.row.original || props.row || {}
      return h('div', { class: 'flex justify-center' }, h('span', { class: 'text-sm font-medium text-gray-700 dark:text-gray-300' }, row.version || ''))
    }
  },
  {
    id: 'download',
    accessorKey: 'downloadUrl',
    header: t('chromeExtension.tableDownload'),
    width: '55%',
    minWidth: '140px',
    meta: { align: 'center' },
    cellFormatter: (props) => {
      const row = props.row.original || props.row || {}
      const url = row.downloadUrl
      if (url) {
        return h('div', { class: 'flex justify-center' }, h('a', {
          href: url,
          target: '_blank',
          rel: 'noopener noreferrer',
          onClick: async (event) => {
            event.preventDefault()
            try {
              const response = await fetch(url)
              if (!response.ok) {
                throw new Error(`Download endpoint returned ${response.status}`)
              }
              const blob = await response.blob()
              const fileUrl = window.URL.createObjectURL(blob)
              const a = document.createElement('a')
              a.href = fileUrl
              a.download = `${row.tagName || 'release'}.tar.gz`
              document.body.appendChild(a)
              a.click()
              a.remove()
              window.URL.revokeObjectURL(fileUrl)
            } catch (err) {
              console.error('Download failed:', err)
            }
          },
          class: 'inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 transition-colors shrink-0 w-fit'
        }, [h(Icon, { name: 'download', size: 'sm', class: 'w-3.5 h-3.5 shrink-0' }), t('chromeExtension.downloadZip')]))
      }
      return h('div', { class: 'flex justify-center' }, h('span', { class: 'text-xs text-gray-400 dark:text-gray-500' }, t('chromeExtension.noZipAsset')))
    }
  }
])

function handleBack() {
  router.push('/dashboard')
}
</script>

<style scoped>
.chrome-extension-table-wrapper {
  width: 100%;
  min-width: 0;
}
:deep(.chrome-extension-releases-table) {
  width: 100% !important;
  table-layout: fixed;
}
:deep(.chrome-extension-releases-table th),
:deep(.chrome-extension-releases-table td) {
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  text-align: center;
}
</style>
